import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { SettingsSection } from "component/shared/settings-page";
import { PlatformChoice } from "type/platform-choice";
import { WIX_APP_MARKET_URL } from "../constants";
import { PlatformIcon } from "../platform-icon";
import { styles } from "./style";
import { WebsiteUrlRow } from "./website-url-row";

interface Props extends WithStyles<typeof styles> {
	platform: PlatformChoice;
	/** "WordPress (Hosted by iHF)" rather than the plugin label (VPLAT 2). */
	omnipress: boolean;
	/** v9 shows only the platform and key: no Edit and no setup action (VPLAT 31). */
	v10: boolean;
	showRegistrationKey: boolean;
	registrationKey: string;
	/**
	 * The host the Optima Express plugin last registered from (MAX-4506). Unset until the plugin
	 * has registered, and then the row is left out.
	 */
	registeredHost?: string;
	websiteUrl: string;
	/**
	 * A Wix client that has installed the app has nothing to set up here, and can't change platform
	 * from CP (as today) while it's installed (VPLAT 11).
	 */
	wixInstalled: boolean;
	/**
	 * Other only: the Set Up IDX wizard was finished, i.e. the Default IDX Page (always enabled) and the
	 * Search page are both enabled. It records our own setup steps, not a connection check.
	 */
	setupComplete: boolean;
	onCopyRegistrationKey: () => void;
	onEditPlatform: () => void;
	onSetUp: () => void;
	/** Saves a new Website URL from the Other platform's row (MAX-4576); reject to keep it open with an error. */
	onSaveWebsiteUrl: (websiteUrl: string) => Promise<void>;
}

// Only WordPress says more than the choice's own label: which of the two ways it runs.
const platformLabel = (platform: PlatformChoice, omnipress: boolean) => {
	if (platform.wordpress) {
		return omnipress ? "WordPress (Hosted by iHF)" : "WordPress (Optima Express Plugin)";
	}
	return platform.label;
};

const SETUP_LABEL_ID = "settings-idx-basic-setup";
/**
 * Wix installs from the App Market, so the page shows the install link directly, as WordPress shows
 * its key. Basic setup › Get started (which opens the wizard's Wix step) is kept behind this switch
 * rather than removed (Dave's pass).
 */
const SHOW_WIX_GET_STARTED = false;
const SETUP_BANNER = "Your IDX setup isn't finished yet. Complete setup to display listing data on your website.";
const REGISTRATION_KEY_INSTRUCTION =
	"In WordPress, go to Optima Express → Register to enter this key. You'll need it again if the plugin is reinstalled or unregistered.";

// The Website block (VPLAT 1, 2, 11, 31; "Connection" in the Figma). The first release has no Status
// row (D1): nothing reports connection yet (SPFF-1/2/3). An HTML site gets the Figma's banner
// (VPLAT 15) until its setup is finished, worded around setup rather than connection. WordPress has
// no wizard: the key and its instruction are all it needs, set off from the platform rows by a
// divider, with the registered site above the divider once the plugin has registered (MAX-4506,
// `3727:7148`). A Wix site without the app gets the App Market install link the same way (Dave's pass).
const Component: React.FC<Props> = ({
	platform,
	omnipress,
	v10,
	showRegistrationKey,
	registrationKey,
	registeredHost,
	websiteUrl,
	wixInstalled,
	setupComplete,
	onCopyRegistrationKey,
	onEditPlatform,
	onSetUp,
	onSaveWebsiteUrl,
	classes = {},
}) => (
	<SettingsSection refreshed title="Website">
		<Mui.Box className={classes.connectionRows}>
			{v10 && platform.other && !setupComplete && (
				<Mui.Box className={classes.setupBanner} role="status">
					<Icons.AlertTriangle size={16} className={classes.setupBannerIcon} />
					<Mui.Typography className={classes.setupBannerText}>{SETUP_BANNER}</Mui.Typography>
					<Mui.Button variant="contained" color="primary" onClick={onSetUp}>
						Get Started
					</Mui.Button>
				</Mui.Box>
			)}
			<Mui.Box className={classes.connectionRow}>
				<Mui.Typography className={classes.rowLabel}>Platform</Mui.Typography>
				<Mui.Box className={classes.rowValue}>
					<Mui.Box className={classes.platform}>
						<PlatformIcon choice={platform} />
						<Mui.Typography className={classes.platformLabel}>
							{platformLabel(platform, omnipress)}
						</Mui.Typography>
					</Mui.Box>
					{v10 && !(platform.wix && wixInstalled) && (
						<Mui.Button
							variant="text"
							color="primary"
							startIcon={<Icons.Edit size={16} />}
							className={classes.inlineAction}
							onClick={onEditPlatform}
						>
							Edit
						</Mui.Button>
					)}
				</Mui.Box>
			</Mui.Box>
			{/* An HTML site sets its own address, and the scripts only load IDX where it matches (MAX-4576). WordPress and Wix get theirs from the plugin or app. */}
			{platform.other && <WebsiteUrlRow websiteUrl={websiteUrl} onSave={onSaveWebsiteUrl} classes={classes} />}
			{platform.wix && websiteUrl && (
				<Mui.Box className={classes.connectionRow}>
					<Mui.Typography className={classes.rowLabel}>Website URL</Mui.Typography>
					<Mui.Typography className={classes.rowText}>{websiteUrl}</Mui.Typography>
				</Mui.Box>
			)}
			{showRegistrationKey && registeredHost && (
				<Mui.Box className={classes.connectionRow}>
					<Mui.Typography className={classes.rowLabel}>Registered URL</Mui.Typography>
					<Mui.Typography className={classes.rowText}>{registeredHost}</Mui.Typography>
				</Mui.Box>
			)}
			{showRegistrationKey && <Mui.Divider className={classes.divider} />}
			{showRegistrationKey && (
				<Mui.Box className={classes.keyGroup}>
					<Mui.Typography className={classes.keyInstruction}>{REGISTRATION_KEY_INSTRUCTION}</Mui.Typography>
					<Mui.Box className={classes.connectionRow}>
						<Mui.Typography className={classes.rowLabel}>Optima Express Registration Key</Mui.Typography>
						<Mui.Box className={classes.rowValue}>
							<Mui.Typography className={classes.rowText}>{registrationKey}</Mui.Typography>
							<Mui.Button
								variant="text"
								color="primary"
								startIcon={<Icons.Copy size={16} />}
								className={classes.inlineAction}
								onClick={onCopyRegistrationKey}
							>
								Copy
							</Mui.Button>
						</Mui.Box>
					</Mui.Box>
				</Mui.Box>
			)}
			{v10 && platform.wix && !wixInstalled && (
				<Mui.Box className={classes.keyGroup}>
					<Mui.Typography className={classes.rowInstruction}>
						Wix sites use the iHomefinder app from the Wix App Market. Install it to get started.
					</Mui.Typography>
					<Mui.Box>
						<Mui.Button
							variant="text"
							color="primary"
							className={classes.inlineAction}
							href={WIX_APP_MARKET_URL}
							target="_blank"
							rel="noopener noreferrer"
							endIcon={<Icons.ExternalLink size={16} />}
						>
							Install iHomefinder App for Wix
						</Mui.Button>
					</Mui.Box>
				</Mui.Box>
			)}
			{SHOW_WIX_GET_STARTED && v10 && platform.wix && !wixInstalled && (
				<Mui.Box className={classes.setupRow}>
					<Mui.Typography id={SETUP_LABEL_ID} className={classes.rowLabel}>Basic setup</Mui.Typography>
					<Mui.Button
						variant="contained"
						color="primary"
						aria-describedby={SETUP_LABEL_ID}
						onClick={onSetUp}
					>
						Get started
					</Mui.Button>
				</Mui.Box>
			)}
		</Mui.Box>
	</SettingsSection>
);

export const ConnectionSection = withStyles(Component, styles);
